import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { types as sdkTypes } from '../../util/sdkLoader';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { propTypes } from '../../util/types';
import { ensureListing, ensureTransaction, ensureOwnListing } from '../../util/data';
import { dateFromAPIToLocalNoon } from '../../util/dates';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  createSlug,
} from '../../util/urlHelpers';
import { txIsPaymentPending, txIsLegacyProProcess } from '../../util/transaction';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import {
  NamedRedirect,
  TransactionPanel,
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
} from '../../components';
import { TopbarContainer } from '../../containers';

import {
  acceptSale,
  declineSale,
  releaseFunds,
  loadData,
  setInitialValues,
  sendMessage,
  sendReview,
  fetchMoreMessages,
  fetchTransactionLineItems,
  sendCustomOffer,
  acceptCustomProOffer,
  declineProjectBid,
  sendProjectBid,
  showProfileListing,
  sendEnquiryCustomer,
  sendEnquiryProvider,
  jobReady,
  jobReadyCustomer,
  jobReadyPro,
  acceptProJob,
  jobDoneReleaseFunds,
  downloadAttachment,
  downloadReceipt,
  openAttachment,
  fetchAttachmentPreviewUrl,
  acceptOfferInvoice,
  jobDoneInvoice,
  acceptJobInvoice,
  jobDoneAcceptInvoice,
  fetchVatPreview,
  fetchTransaction,
} from './TransactionPage.duck';
import { saveBillingDetails } from '../../ducks/user.duck';
import {
  fetchStripeAccount,
  getStripeConnectAccountLink,
} from '../../ducks/stripeConnectAccount.duck';
import {
  requestInvoicing,
  lookupInvoicingCompany,
  clearInvoicingRequest,
} from '../../ducks/invoicing.duck';
import {
  uploadAttachment,
  removeAttachment,
  stagedAttachmentsList,
  SCOPE_TRANSACTION_PAGE,
  SCOPE_CUSTOM_OFFER,
  seedStagedAttachments,
  clearStagedAttachments,
} from '../../ducks/messageAttachments.duck';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import css from './TransactionPage.css';

const { Money, UUID } = sdkTypes;

const PROVIDER = 'provider';
const CUSTOMER = 'customer';

// TransactionPage handles data loading for Sale and Order views to transaction pages in Inbox.
export const TransactionPageComponent = props => {
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const {
    currentUser,
    initialMessageFailedToTransaction,
    savePaymentMethodFailed,
    fetchMessagesError,
    fetchMessagesInProgress,
    totalMessagePages,
    oldestMessagePageFetched,
    fetchTransactionError,
    history,
    intl,
    messages,
    onManageDisableScrolling,
    onSendMessage,
    onSendReview,
    onShowMoreMessages,
    onUploadAttachment,
    onRemoveAttachment,
    onDownloadAttachment,
    onOpenAttachment,
    onFetchAttachmentPreview,
    attachmentPreviewUrls,
    stagedAttachments,
    offerStagedAttachments,
    onUploadOfferAttachment,
    onRemoveOfferAttachment,
    onSeedOfferAttachments,
    onClearOfferAttachments,
    downloadAttachmentInProgressIds,
    downloadAttachmentError,
    onDownloadReceipt,
    downloadReceiptInProgressId,
    downloadReceiptFailedId,
    params,
    scrollingDisabled,
    sendMessageError,
    sendMessageInProgress,
    sendReviewError,
    ownReview,
    sendReviewInProgress,
    transaction,
    transactionRole,
    acceptInProgress,
    acceptSaleError,
    declineInProgress,
    declineSaleError,
    onAcceptSale,
    onDeclineSale,
    onJobReady,
    onJobReadyCustomer,
    onJobReadyPro,
    onAcceptProJob,
    timeSlots,
    fetchTimeSlotsError,
    processTransitions,
    callSetInitialValues,
    onInitializeCardPaymentData,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    onReleaseFunds,
    releaseFundsInProgress,
    releaseFundsError,
    onSendCustomOffer,
    onAcceptCustomProOffer,
    onAcceptOfferInvoice,
    acceptOfferInvoiceInProgress,
    acceptOfferInvoiceError,
    acceptCustomProOfferError,
    onJobDoneInvoice,
    onAcceptJobInvoice,
    onJobDoneAcceptInvoice,
    onRequestInvoicing,
    onLookupInvoicingCompany,
    onClearInvoicingRequest,
    requestInvoicingInProgress,
    requestInvoicingError,
    requestInvoicingResult,
    lookupInvoicingCompanyInProgress,
    lookupInvoicingCompanyError,
    lookupInvoicingCompanyResult,
    vatPreview,
    vatPreviewInProgress,
    vatPreviewError,
    onFetchVatPreview,
    onSaveBillingDetails,
    saveBillingDetailsInProgress,
    saveBillingDetailsError,
    sendCustomOfferInProgress,
    sendCustomOfferError,
    onDeclineProjectBid,
    declineProjectBidInProgress,
    declineProjectBidError,
    onSendProjectBid,
    sendProjectBidInProgress,
    sendProjectBidError,
    onShowProfileListing,
    showProfileListingInProgress,
    showProfileListingError,
    projectTransaction,
    sendEnquiryErrorListing,
    sendEnquiryErrorProject,
    onJobDoneReleaseFunds,
    jobDoneReleaseFundsInProgress,
    jobDoneReleaseFundsError,
    onUpdateProfile,
    applicationTransaction,
    applicationPortfolioWorks,
    skillOptions,
    toolOptions,
    stripeAccount,
    getAccountLinkInProgress,
    onFetchStripeAccount,
    onGetStripeConnectAccountLink,
  } = props;

  const currentTransaction = ensureTransaction(transaction);
  const currentListing = ensureListing(currentTransaction.listing);
  const isProviderRole = transactionRole === PROVIDER;
  const isCustomerRole = transactionRole === CUSTOMER;

  const redirectToCheckoutPageWithInitialValues = (initialValues, listing) => {
    if (!mountedRef.current) return;

    const routes = routeConfiguration();
    // Customize checkout page state with current listing and selected bookingDates
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);
    callSetInitialValues(setInitialValues, initialValues);

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    // Redirect to CheckoutPage
    history.push(
      createResourceLocatorString(
        'CheckoutPage',
        routes,
        { id: currentListing.id.uuid, slug: createSlug(currentListing.attributes.title) },
        {}
      )
    );
  };

  const onSubmitEnquiryCustomer = values => {
    const { history, params, onSendEnquiryCustomer, getListing, getOwnListing } = props;
    const routes = routeConfiguration();
    const currentListingID = ensureListing(currentTransaction.listing);
    const listingId = new UUID(currentListingID.id.uuid);
    const { message } = values;
    const isPendingApprovalVariant = params.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
    const isDraftVariant = params.variant === LISTING_PAGE_DRAFT_VARIANT;
    const currentListing =
      isPendingApprovalVariant || isDraftVariant
        ? ensureOwnListing(getOwnListing(listingId))
        : ensureListing(getListing(listingId));
    const author = currentListing && currentListing.author;
    const isLightEntrepreneur =
      author &&
      author.attributes &&
      author.attributes.profile.publicData &&
      author.attributes.profile.publicData.account_role === 'light-entrepreneur';

    onSendEnquiryCustomer(listingId, isLightEntrepreneur, message.trim())
      .then(txId => {
        if (!mountedRef.current) return;

        // this.setState({ enquiryModalOpen: false });

        // if (typeof window === 'object') {
        //   window.dataLayer.push({
        //     event: 'contact_author',
        //     ecommerce: {
        //       items: [
        //         {
        //           item_name: currentListing.attributes.title,
        //           item_id: currentListing.id.uuid,
        //           price: '' + currentListing.attributes.price.amount / 100,
        //           item_brand: currentListing.author.attributes.profile.displayName,
        //           item_category: currentListing.attributes.publicData.category,
        //           item_list_name: 'listing_page',
        //         },
        //       ],
        //     },
        //   });
        // }

        // Redirect to OrderDetailsPage
        history.push(
          createResourceLocatorString('OrderDetailsPage', routes, { id: txId.uuid }, {})
        );
      })
      .catch(() => {
        // Ignore, error handling in duck file
      });
  };

  const handlePackageSubmit = values => {
    if (!mountedRef.current) return;

    const { history, getListing, callSetInitialValues, onInitializeCardPaymentData } = props;
    const currentListingID = ensureListing(currentTransaction.listing);
    const listingId = new UUID(currentListingID.id.uuid);
    const listing = getListing(listingId);

    const { ...bookingData } = values;
    let startDate = new Date();
    startDate.setDate(startDate.getDate() + 1);
    let endDate = new Date();
    endDate.setDate(endDate.getDate() + 2);

    const initialValues = {
      listing,
      bookingData: bookingData,
      bookingDates: {
        bookingStart: startDate,
        bookingEnd: endDate,
      },
      confirmPaymentError: null,
    };

    const saveToSessionStorage = !props.currentUser;

    const routes = routeConfiguration();
    // Customize checkout page state with current listing and selected bookingDates
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);

    callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    // Redirect to CheckoutPage
    history.push(
      createResourceLocatorString(
        'CheckoutPage',
        routes,
        { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
        {}
      )
    );
  };

  const onSubmitEnquiryProvider = values => {
    const { history, params, onSendEnquiryProvider, getListing, getOwnListing } = props;
    const routes = routeConfiguration();
    const currentListingID = ensureListing(currentTransaction.listing);
    const listingId = new UUID(currentListingID.id.uuid);
    const { message } = values;
    const isPendingApprovalVariant = params.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
    const isDraftVariant = params.variant === LISTING_PAGE_DRAFT_VARIANT;
    const currentListing =
      isPendingApprovalVariant || isDraftVariant
        ? ensureOwnListing(getOwnListing(listingId))
        : ensureListing(getListing(listingId));

    onSendEnquiryProvider(listingId, message.trim())
      .then(txId => {
        if (!mountedRef.current) return;

        // this.setState({ enquiryModalOpen: false });

        if (typeof window === 'object') {
          window.dataLayer.push({
            event: 'contact_author',
            ecommerce: {
              items: [
                {
                  item_name: currentListing.attributes.title,
                  item_id: currentListing.id.uuid,
                  price: '' + currentListing.attributes.price.amount / 100,
                  item_brand: currentListing.author.attributes.profile.displayName,
                  item_category: currentListing.attributes.publicData.category,
                  item_list_name: 'listing_page',
                },
              ],
            },
          });
        }

        // Redirect to OrderDetailsPage
        history.push(
          createResourceLocatorString('OrderDetailsPage', routes, { id: txId.uuid }, {})
        );
      })
      .catch(() => {
        // Ignore, error handling in duck file
      });
  };

  const onSubmitNewProjectEnquiry = values => {
    const { history, params, onSendEnquiryProvider, getListing, getOwnListing } = props;
    const routes = routeConfiguration();
    const currentListingID = ensureListing(projectTransaction.listing);
    const listingId = new UUID(currentListingID.id.uuid);
    const { message } = values;
    const isPendingApprovalVariant = params.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
    const isDraftVariant = params.variant === LISTING_PAGE_DRAFT_VARIANT;
    const currentListing =
      isPendingApprovalVariant || isDraftVariant
        ? ensureOwnListing(getOwnListing(listingId))
        : ensureListing(getListing(listingId));

    onSendEnquiryProvider(listingId, message.trim())
      .then(txId => {
        if (!mountedRef.current) return;

        // this.setState({ enquiryModalOpen: false });

        if (typeof window === 'object') {
          window.dataLayer.push({
            event: 'contact_author',
            content_type: 'rehire',
            ecommerce: {
              items: [
                {
                  item_name: currentListing.attributes.title,
                  item_id: currentListing.id.uuid,
                  price: '' + currentListing.attributes.price.amount / 100,
                  item_brand: currentListing.author.attributes.profile.displayName,
                  item_category: currentListing.attributes.publicData.category,
                  item_list_name: 'listing_page',
                },
              ],
            },
          });
        }

        // Redirect to OrderDetailsPage
        history.push(
          createResourceLocatorString('OrderDetailsPage', routes, { id: txId.uuid }, {})
        );
      })
      .catch(() => {
        // Ignore, error handling in duck file
      });
  };

  // If payment is pending, redirect to CheckoutPage
  if (
    txIsPaymentPending(currentTransaction) &&
    isCustomerRole &&
    currentTransaction.attributes.lineItems
  ) {
    const currentBooking = ensureListing(currentTransaction.booking);

    const initialValues = {
      listing: currentListing,
      // Transaction with payment pending should be passed to CheckoutPage
      transaction: currentTransaction,
      // Original bookingData content is not available,
      // but it is already used since booking is created.
      // (E.g. quantity is used when booking is created.)
      bookingData: {},
      bookingDates: {
        bookingStart: dateFromAPIToLocalNoon(currentBooking.attributes.start),
        bookingEnd: dateFromAPIToLocalNoon(currentBooking.attributes.end),
      },
    };

    redirectToCheckoutPageWithInitialValues(initialValues, currentListing);
  }

  // Customer can create a booking, if the tx is in "enquiry" state.
  const handleSubmitBookingRequest = values => {
    return new Promise((resolve, reject) => {
      try {
        const { skipAddToCartEvent = false, paymentMethod = 'card', ...bookingData } = values;

        const startTime = new Date();
        startTime.setUTCMinutes(Math.ceil((startTime.getUTCMinutes() + 5) / 5) * 5, 0, 0);
        const endTime = new Date();
        endTime.setUTCMinutes(Math.ceil((endTime.getUTCMinutes() + 10) / 5) * 5, 0, 0);

        const priceAmount =
          values.pricingPackage === 'package1'
            ? currentListing.attributes?.price?.amount
            : values.pricingPackage &&
              currentListing.attributes.publicData[`${values.pricingPackage}Price`]
            ? currentListing.attributes.publicData[`${values.pricingPackage}Price`]
            : 0;
        const pricingPackageName =
          values.pricingPackage &&
          currentListing.attributes.publicData[`${values.pricingPackage}Name`]
            ? currentListing.attributes.publicData[`${values.pricingPackage}Name`]
            : '';

        // Add to cart event if required
        if (!skipAddToCartEvent) {
          if (typeof window === 'object') {
            window.dataLayer.push({
              event: 'add_to_cart',
              ecommerce: {
                items: [
                  {
                    item_name: currentListing.attributes.title,
                    item_id: currentListing.id.uuid,
                    price: '' + priceAmount / 100,
                    item_brand: currentTransaction.provider.attributes.profile.displayName,
                    item_category: currentListing.attributes.publicData.category,
                    item_list_name: 'chat_window',
                    item_variant: pricingPackageName,
                    custom_offer: 'false',
                    custom_offer_value: null,
                    quantity: '1',
                  },
                ],
              },
            });
          }
        }

        const initialValues = {
          listing: currentListing,
          transaction: currentTransaction,
          bookingData,
          bookingDates: {
            bookingStart: startTime,
            bookingEnd: endTime,
          },
          confirmPaymentError: null,
        };

        const isLegacyPro = txIsLegacyProProcess(currentTransaction);
        const settle = promise =>
          promise
            .then(result => {
              if (mountedRef.current) resolve(result);
            })
            .catch(error => {
              if (mountedRef.current) reject(error);
            });

        if (isLegacyPro) {
          // Transactions on the retired pro process keep their own accept transition.
          settle(
            onAcceptCustomProOffer(
              currentTransaction.id.uuid,
              bookingData.description,
              priceAmount,
              intl
            )
          );
        } else if (paymentMethod === 'invoice') {
          // Privileged server transition; price and billing come from the server.
          settle(onAcceptOfferInvoice(currentTransaction.id));
        } else if (mountedRef.current) {
          // Card: redirect to the checkout page.
          resolve();
          redirectToCheckoutPageWithInitialValues(initialValues, currentListing);
        }
      } catch (error) {
        reject(error); // If an error occurs, reject the promise
      }
    });
  };

  // Customer can create a booking, if the tx is in "enquiry" state.
  const handleSubmitAcceptBidRequest = values => {
    onShowProfileListing(currentTransaction.customer.id)
      .then(profileListing => {
        if (!mountedRef.current) return;

        const { ...bookingData } = values;
        const startTime = new Date();
        startTime.setUTCMinutes(Math.ceil((startTime.getUTCMinutes() + 5) / 5) * 5, 0, 0);
        const endTime = new Date();
        endTime.setUTCMinutes(Math.ceil((endTime.getUTCMinutes() + 10) / 5) * 5, 0, 0);

        profileListing.attributes.price = new Money(
          currentTransaction.attributes.protectedData.projectBidAmount,
          'EUR'
        );

        const initialValues = {
          listing: profileListing,
          bookingData,
          bookingDates: {
            bookingStart: startTime,
            bookingEnd: endTime,
          },
          confirmPaymentError: null,
          projectTransaction: currentTransaction,
        };

        const routes = routeConfiguration();
        // Customize checkout page state with current listing and selected bookingDates
        const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);

        callSetInitialValues(setInitialValues, initialValues);

        // Clear previous Stripe errors from store if there is any
        onInitializeCardPaymentData();

        // Redirect to CheckoutPage
        history.push(
          createResourceLocatorString(
            'CheckoutPage',
            routes,
            { id: profileListing.id.uuid, slug: createSlug(profileListing.attributes.title) },
            {}
          )
        );
      })
      .catch(error => {
        // Handle error silently if component is unmounted
        if (mountedRef.current) {
          console.error('Error in handleSubmitAcceptBidRequest:', error);
        }
      });
  };

  // Redirect users with someone else's direct link to their own inbox/sales or inbox/orders page.
  const isDataAvailable =
    currentUser &&
    currentTransaction.id &&
    currentTransaction.id.uuid === params.id &&
    currentTransaction.attributes.lineItems &&
    currentTransaction.customer &&
    currentTransaction.provider &&
    !fetchTransactionError;

  const isOwnSale =
    isDataAvailable &&
    isProviderRole &&
    currentUser.id.uuid === currentTransaction.provider.id.uuid;
  const isOwnOrder =
    isDataAvailable &&
    isCustomerRole &&
    currentUser.id.uuid === currentTransaction.customer.id.uuid;

  if (isDataAvailable && isProviderRole && !isOwnSale) {
    // eslint-disable-next-line no-console
    console.error('Tried to access a sale that was not owned by the current user');
    return <NamedRedirect name="InboxPage" params={{ tab: 'sales' }} />;
  } else if (isDataAvailable && isCustomerRole && !isOwnOrder) {
    // eslint-disable-next-line no-console
    console.error('Tried to access an order that was not owned by the current user');
    return <NamedRedirect name="InboxPage" params={{ tab: 'orders' }} />;
  }

  const fetchErrorMessage = isCustomerRole
    ? 'TransactionPage.fetchOrderFailed'
    : 'TransactionPage.fetchSaleFailed';
  const loadingMessage = isCustomerRole
    ? 'TransactionPage.loadingOrderData'
    : 'TransactionPage.loadingSaleData';

  const loadingOrFailedFetching = fetchTransactionError ? (
    <p className={css.error}>
      <FormattedMessage id={`${fetchErrorMessage}`} />
    </p>
  ) : (
    <p className={css.loading}>
      <FormattedMessage id={`${loadingMessage}`} />
    </p>
  );

  const initialMessageFailed = !!(
    initialMessageFailedToTransaction &&
    currentTransaction.id &&
    initialMessageFailedToTransaction.uuid === currentTransaction.id.uuid
  );

  // TransactionPanel is presentational component
  // that currently handles showing everything inside layout's main view area.
  const panel = isDataAvailable ? (
    <TransactionPanel
      currentUser={currentUser}
      transaction={currentTransaction}
      fetchMessagesInProgress={fetchMessagesInProgress}
      totalMessagePages={totalMessagePages}
      oldestMessagePageFetched={oldestMessagePageFetched}
      messages={messages}
      initialMessageFailed={initialMessageFailed}
      savePaymentMethodFailed={savePaymentMethodFailed}
      fetchMessagesError={fetchMessagesError}
      sendMessageInProgress={sendMessageInProgress}
      sendMessageError={sendMessageError}
      sendReviewInProgress={sendReviewInProgress}
      sendReviewError={sendReviewError}
      ownReview={ownReview}
      onManageDisableScrolling={onManageDisableScrolling}
      onShowMoreMessages={onShowMoreMessages}
      onSendMessage={onSendMessage}
      onUploadAttachment={onUploadAttachment}
      onRemoveAttachment={onRemoveAttachment}
      onDownloadAttachment={onDownloadAttachment}
      onOpenAttachment={onOpenAttachment}
      onFetchAttachmentPreview={onFetchAttachmentPreview}
      attachmentPreviewUrls={attachmentPreviewUrls}
      stagedAttachments={stagedAttachments}
      offerStagedAttachments={offerStagedAttachments}
      onUploadOfferAttachment={onUploadOfferAttachment}
      onRemoveOfferAttachment={onRemoveOfferAttachment}
      onSeedOfferAttachments={onSeedOfferAttachments}
      onClearOfferAttachments={onClearOfferAttachments}
      downloadAttachmentInProgressIds={downloadAttachmentInProgressIds}
      downloadAttachmentError={downloadAttachmentError}
      onDownloadReceipt={onDownloadReceipt}
      downloadReceiptInProgressId={downloadReceiptInProgressId}
      downloadReceiptFailedId={downloadReceiptFailedId}
      onSendReview={onSendReview}
      transactionRole={transactionRole}
      onAcceptSale={onAcceptSale}
      onDeclineSale={onDeclineSale}
      onJobReady={onJobReady}
      onJobReadyCustomer={onJobReadyCustomer}
      onJobReadyPro={onJobReadyPro}
      onAcceptProJob={onAcceptProJob}
      onAcceptOfferInvoice={onAcceptOfferInvoice}
      acceptOfferInvoiceInProgress={acceptOfferInvoiceInProgress}
      acceptOfferInvoiceError={acceptOfferInvoiceError}
      acceptCustomProOfferError={acceptCustomProOfferError}
      onJobDoneInvoice={onJobDoneInvoice}
      onAcceptJobInvoice={onAcceptJobInvoice}
      onJobDoneAcceptInvoice={onJobDoneAcceptInvoice}
      onRequestInvoicing={onRequestInvoicing}
      onLookupInvoicingCompany={onLookupInvoicingCompany}
      onClearInvoicingRequest={onClearInvoicingRequest}
      requestInvoicingInProgress={requestInvoicingInProgress}
      requestInvoicingError={requestInvoicingError}
      requestInvoicingResult={requestInvoicingResult}
      lookupInvoicingCompanyInProgress={lookupInvoicingCompanyInProgress}
      lookupInvoicingCompanyError={lookupInvoicingCompanyError}
      lookupInvoicingCompanyResult={lookupInvoicingCompanyResult}
      vatPreview={vatPreview}
      vatPreviewInProgress={vatPreviewInProgress}
      vatPreviewError={vatPreviewError}
      onFetchVatPreview={onFetchVatPreview}
      onSaveBillingDetails={onSaveBillingDetails}
      saveBillingDetailsInProgress={saveBillingDetailsInProgress}
      saveBillingDetailsError={saveBillingDetailsError}
      acceptInProgress={acceptInProgress}
      declineInProgress={declineInProgress}
      acceptSaleError={acceptSaleError}
      declineSaleError={declineSaleError}
      nextTransitions={processTransitions}
      onSubmitBookingRequest={handleSubmitBookingRequest}
      timeSlots={timeSlots}
      fetchTimeSlotsError={fetchTimeSlotsError}
      onFetchTransactionLineItems={onFetchTransactionLineItems}
      lineItems={lineItems}
      fetchLineItemsInProgress={fetchLineItemsInProgress}
      fetchLineItemsError={fetchLineItemsError}
      onReleaseFunds={onReleaseFunds}
      releaseFundsInProgress={releaseFundsInProgress}
      releaseFundsError={releaseFundsError}
      onSendCustomOffer={onSendCustomOffer}
      sendCustomOfferInProgress={sendCustomOfferInProgress}
      sendCustomOfferError={sendCustomOfferError}
      onDeclineProjectBid={onDeclineProjectBid}
      declineProjectBidInProgress={declineProjectBidInProgress}
      declineProjectBidError={declineProjectBidError}
      callSetInitialValues={callSetInitialValues}
      setInitialValues={setInitialValues}
      onSendProjectBid={onSendProjectBid}
      sendProjectBidInProgress={sendProjectBidInProgress}
      sendProjectBidError={sendProjectBidError}
      onSubmitAcceptBidRequest={handleSubmitAcceptBidRequest}
      showProfileListingInProgress={showProfileListingInProgress}
      showProfileListingError={showProfileListingError}
      projectTransaction={projectTransaction}
      onSubmitEnquiryCustomer={onSubmitEnquiryCustomer}
      onSubmitEnquiryProvider={onSubmitEnquiryProvider}
      onSubmitNewProjectEnquiry={onSubmitNewProjectEnquiry}
      sendEnquiryErrorListing={sendEnquiryErrorListing}
      sendEnquiryErrorProject={sendEnquiryErrorProject}
      handlePackageSubmit={handlePackageSubmit}
      onJobDoneReleaseFunds={onJobDoneReleaseFunds}
      jobDoneReleaseFundsInProgress={jobDoneReleaseFundsInProgress}
      jobDoneReleaseFundsError={jobDoneReleaseFundsError}
      onUpdateProfile={onUpdateProfile}
      applicationTransaction={applicationTransaction}
      applicationPortfolioWorks={applicationPortfolioWorks}
      skillOptions={skillOptions}
      toolOptions={toolOptions}
      stripeAccount={stripeAccount}
      getAccountLinkInProgress={getAccountLinkInProgress}
      onFetchStripeAccount={onFetchStripeAccount}
      onGetStripeConnectAccountLink={onGetStripeConnectAccountLink}
    />
  ) : (
    loadingOrFailedFetching
  );

  return (
    <Page
      title={intl.formatMessage(
        { id: 'TransactionPage.title' },
        { title: currentTransaction?.provider?.attributes?.profile?.displayName }
      )}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.root}>{panel}</div>
        </LayoutWrapperMain>
      </LayoutSingleColumn>
    </Page>
  );
};

TransactionPageComponent.defaultProps = {
  currentUser: null,
  fetchTransactionError: null,
  acceptSaleError: null,
  declineSaleError: null,
  transaction: null,
  fetchMessagesError: null,
  initialMessageFailedToTransaction: null,
  savePaymentMethodFailed: false,
  sendMessageError: null,
  timeSlots: null,
  fetchTimeSlotsError: null,
  lineItems: null,
  fetchLineItemsError: null,
  sendEnquiryErrorListing: null,
  sendEnquiryErrorProject: null,
};

const { bool, func, oneOf, shape, string, array, arrayOf, number, object } = PropTypes;

TransactionPageComponent.propTypes = {
  params: shape({ id: string }).isRequired,
  transactionRole: oneOf([PROVIDER, CUSTOMER]).isRequired,
  currentUser: propTypes.currentUser,
  fetchTransactionError: propTypes.error,
  acceptSaleError: propTypes.error,
  declineSaleError: propTypes.error,
  acceptInProgress: bool.isRequired,
  declineInProgress: bool.isRequired,
  onAcceptSale: func.isRequired,
  onDeclineSale: func.isRequired,
  onJobReady: func.isRequired,
  onJobReadyCustomer: func.isRequired,
  onJobReadyPro: func.isRequired,
  onAcceptProJob: func.isRequired,
  onAcceptOfferInvoice: func.isRequired,
  acceptOfferInvoiceInProgress: bool.isRequired,
  acceptOfferInvoiceError: propTypes.error,
  acceptCustomProOfferError: propTypes.error,
  onJobDoneInvoice: func.isRequired,
  onAcceptJobInvoice: func.isRequired,
  onJobDoneAcceptInvoice: func.isRequired,
  onRequestInvoicing: func.isRequired,
  onLookupInvoicingCompany: func.isRequired,
  onClearInvoicingRequest: func.isRequired,
  requestInvoicingInProgress: bool.isRequired,
  requestInvoicingError: propTypes.error,
  requestInvoicingResult: object,
  lookupInvoicingCompanyInProgress: bool.isRequired,
  lookupInvoicingCompanyError: propTypes.error,
  lookupInvoicingCompanyResult: object,
  vatPreview: object,
  vatPreviewInProgress: bool.isRequired,
  vatPreviewError: propTypes.error,
  onFetchVatPreview: func.isRequired,
  onSaveBillingDetails: func.isRequired,
  getAccountLinkInProgress: bool,
  onFetchStripeAccount: func,
  onGetStripeConnectAccountLink: func,
  saveBillingDetailsInProgress: bool.isRequired,
  saveBillingDetailsError: propTypes.error,
  scrollingDisabled: bool.isRequired,
  transaction: propTypes.transaction,
  fetchMessagesError: propTypes.error,
  totalMessagePages: number.isRequired,
  oldestMessagePageFetched: number.isRequired,
  messages: arrayOf(propTypes.message).isRequired,
  initialMessageFailedToTransaction: propTypes.uuid,
  savePaymentMethodFailed: bool,
  sendMessageInProgress: bool.isRequired,
  sendMessageError: propTypes.error,
  onShowMoreMessages: func.isRequired,
  onSendMessage: func.isRequired,
  onUploadAttachment: func.isRequired,
  onRemoveAttachment: func.isRequired,
  onDownloadAttachment: func.isRequired,
  onOpenAttachment: func.isRequired,
  onFetchAttachmentPreview: func.isRequired,
  attachmentPreviewUrls: object.isRequired,
  stagedAttachments: array.isRequired,
  offerStagedAttachments: array.isRequired,
  onUploadOfferAttachment: func.isRequired,
  onRemoveOfferAttachment: func.isRequired,
  onSeedOfferAttachments: func.isRequired,
  onClearOfferAttachments: func.isRequired,
  downloadAttachmentInProgressIds: arrayOf(string).isRequired,
  downloadAttachmentError: propTypes.error,
  onDownloadReceipt: func.isRequired,
  downloadReceiptInProgressId: string,
  downloadReceiptFailedId: string,
  timeSlots: arrayOf(propTypes.timeSlot),
  fetchTimeSlotsError: propTypes.error,
  callSetInitialValues: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  onSendEnquiryCustomer: func.isRequired,
  sendEnquiryErrorListing: propTypes.error,
  sendEnquiryErrorProject: propTypes.error,

  // line items
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // Profile update
  onUpdateProfile: func.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    fetchTransactionError,
    acceptSaleError,
    declineSaleError,
    acceptInProgress,
    declineInProgress,
    transactionRef,
    fetchMessagesInProgress,
    fetchMessagesError,
    totalMessagePages,
    oldestMessagePageFetched,
    messages,
    initialMessageFailedToTransaction,
    savePaymentMethodFailed,
    sendMessageInProgress,
    sendMessageError,
    downloadAttachmentInProgressIds,
    downloadAttachmentError,
    downloadReceiptInProgressId,
    downloadReceiptFailedId,
    attachmentPreviewUrls,
    sendReviewInProgress,
    sendReviewError,
    ownReview,
    timeSlots,
    fetchTimeSlotsError,
    processTransitions,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    releaseFundsInProgress,
    releaseFundsError,
    sendCustomOfferInProgress,
    sendCustomOfferError,
    declineProjectBidInProgress,
    declineProjectBidError,
    sendProjectBidInProgress,
    sendProjectBidError,
    showProfileListingInProgress,
    showProfileListingError,
    projectTransactionRef,
    sendEnquiryErrorListing,
    sendEnquiryErrorProject,
    jobDoneReleaseFundsInProgress,
    jobDoneReleaseFundsError,
    applicationTransactionRef,
    applicationPortfolioWorks,
    acceptOfferInvoiceInProgress,
    acceptOfferInvoiceError,
    acceptCustomProOfferError,
    vatPreview,
    vatPreviewInProgress,
    vatPreviewError,
  } = state.TransactionPage;
  const { currentUser, saveBillingDetailsInProgress, saveBillingDetailsError } = state.user;
  const {
    requestInvoicingInProgress,
    requestInvoicingError,
    requestInvoicingResult,
    lookupInvoicingCompanyInProgress,
    lookupInvoicingCompanyError,
    lookupInvoicingCompanyResult,
  } = state.invoicing;
  const { skillOptions, toolOptions } = state.SkillsAndTools;
  const { stripeAccount, getAccountLinkInProgress } = state.stripeConnectAccount;

  const transactions = getMarketplaceEntities(state, transactionRef ? [transactionRef] : []);
  const transaction = transactions.length > 0 ? transactions[0] : null;
  const projectTransactions = getMarketplaceEntities(
    state,
    projectTransactionRef ? [projectTransactionRef] : []
  );
  const projectTransaction = projectTransactions.length > 0 ? projectTransactions[0] : null;
  const applicationTransactions = getMarketplaceEntities(
    state,
    applicationTransactionRef ? [applicationTransactionRef] : []
  );
  const applicationTransaction =
    applicationTransactions.length > 0 ? applicationTransactions[0] : null;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    currentUser,
    getListing,
    getOwnListing,
    fetchTransactionError,
    acceptSaleError,
    declineSaleError,
    acceptInProgress,
    declineInProgress,
    scrollingDisabled: isScrollingDisabled(state),
    transaction,
    fetchMessagesInProgress,
    fetchMessagesError,
    totalMessagePages,
    oldestMessagePageFetched,
    messages,
    initialMessageFailedToTransaction,
    savePaymentMethodFailed,
    sendMessageInProgress,
    sendMessageError,
    stagedAttachments: stagedAttachmentsList(state, SCOPE_TRANSACTION_PAGE),
    offerStagedAttachments: stagedAttachmentsList(state, SCOPE_CUSTOM_OFFER),
    downloadAttachmentInProgressIds,
    downloadAttachmentError,
    downloadReceiptInProgressId,
    downloadReceiptFailedId,
    attachmentPreviewUrls,
    sendReviewInProgress,
    sendReviewError,
    ownReview,
    timeSlots,
    fetchTimeSlotsError,
    processTransitions,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    releaseFundsInProgress,
    releaseFundsError,
    sendCustomOfferInProgress,
    sendCustomOfferError,
    declineProjectBidInProgress,
    declineProjectBidError,
    sendProjectBidInProgress,
    sendProjectBidError,
    showProfileListingInProgress,
    showProfileListingError,
    projectTransaction,
    sendEnquiryErrorListing,
    sendEnquiryErrorProject,
    jobDoneReleaseFundsInProgress,
    jobDoneReleaseFundsError,
    applicationTransaction,
    applicationPortfolioWorks,
    skillOptions,
    toolOptions,
    stripeAccount,
    getAccountLinkInProgress,
    acceptOfferInvoiceInProgress,
    acceptOfferInvoiceError,
    acceptCustomProOfferError,
    requestInvoicingInProgress,
    requestInvoicingError,
    requestInvoicingResult,
    lookupInvoicingCompanyInProgress,
    lookupInvoicingCompanyError,
    lookupInvoicingCompanyResult,
    vatPreview,
    vatPreviewInProgress,
    vatPreviewError,
    saveBillingDetailsInProgress,
    saveBillingDetailsError,
  };
};

export const mapDispatchToProps = dispatch => {
  return {
    onAcceptSale: transactionId => dispatch(acceptSale(transactionId)),
    onDeclineSale: transactionId => dispatch(declineSale(transactionId)),
    onJobReady: transactionId => dispatch(jobReady(transactionId)),
    onJobReadyCustomer: transactionId => dispatch(jobReadyCustomer(transactionId)),
    onJobReadyPro: transactionId => dispatch(jobReadyPro(transactionId)),
    onAcceptProJob: transactionId => dispatch(acceptProJob(transactionId)),
    onAcceptOfferInvoice: transactionId => dispatch(acceptOfferInvoice(transactionId)),
    onJobDoneInvoice: transactionId => dispatch(jobDoneInvoice(transactionId)),
    onAcceptJobInvoice: transactionId => dispatch(acceptJobInvoice(transactionId)),
    onJobDoneAcceptInvoice: transactionId => dispatch(jobDoneAcceptInvoice(transactionId)),
    // The shared duck refetches the current user; on approval this page also
    // refreshes the transaction so its customer relationship carries the flag
    // and the accept button enables without a reload (invoice path spec §3).
    onRequestInvoicing: (payload, txId) =>
      dispatch(requestInvoicing(payload)).then(result => {
        if (result.status === 'approved' && txId) {
          dispatch(fetchTransaction(txId, 'customer'));
        }
        return result;
      }),
    onLookupInvoicingCompany: params => dispatch(lookupInvoicingCompany(params)),
    onClearInvoicingRequest: () => dispatch(clearInvoicingRequest()),
    onFetchVatPreview: (txId, method) => dispatch(fetchVatPreview(txId, method)),
    onSaveBillingDetails: billing => dispatch(saveBillingDetails(billing)),
    onFetchStripeAccount: () => dispatch(fetchStripeAccount()),
    onGetStripeConnectAccountLink: params => dispatch(getStripeConnectAccountLink(params)),
    onReleaseFunds: transactionId => dispatch(releaseFunds(transactionId)),
    onShowMoreMessages: txId => dispatch(fetchMoreMessages(txId)),
    onSendMessage: (txId, message, fileIds) => dispatch(sendMessage(txId, message, fileIds)),
    onUploadAttachment: file => dispatch(uploadAttachment(SCOPE_TRANSACTION_PAGE, file)),
    onRemoveAttachment: tempId => dispatch(removeAttachment(SCOPE_TRANSACTION_PAGE, tempId)),
    onUploadOfferAttachment: file => dispatch(uploadAttachment(SCOPE_CUSTOM_OFFER, file)),
    onRemoveOfferAttachment: tempId => dispatch(removeAttachment(SCOPE_CUSTOM_OFFER, tempId)),
    onSeedOfferAttachments: entries => dispatch(seedStagedAttachments(SCOPE_CUSTOM_OFFER, entries)),
    onClearOfferAttachments: () => dispatch(clearStagedAttachments(SCOPE_CUSTOM_OFFER)),
    onDownloadAttachment: (fileAttachmentId, fileName) =>
      dispatch(downloadAttachment(fileAttachmentId, fileName)),
    onOpenAttachment: (fileAttachmentId, fileName) =>
      dispatch(openAttachment(fileAttachmentId, fileName)),
    onDownloadReceipt: params => dispatch(downloadReceipt(params)),
    onFetchAttachmentPreview: (fileAttachmentId, options) =>
      dispatch(fetchAttachmentPreviewUrl(fileAttachmentId, options)),
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    onSendReview: (role, tx, reviewRating, reviewContent) =>
      dispatch(sendReview(role, tx, reviewRating, reviewContent)),
    callSetInitialValues: (setInitialValues, values) => dispatch(setInitialValues(values)),
    onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
    onFetchTransactionLineItems: (bookingData, listingId, isOwnListing) =>
      dispatch(fetchTransactionLineItems(bookingData, listingId, isOwnListing)),
    onSendCustomOffer: (id, offer, intl) => dispatch(sendCustomOffer(id, offer, intl)),
    onAcceptCustomProOffer: (id, description, customOfferAmount, intl) =>
      dispatch(acceptCustomProOffer(id, description, customOfferAmount, intl)),

    onDeclineProjectBid: transactionId => dispatch(declineProjectBid(transactionId)),
    onSendProjectBid: (listingId, transactionId, description, amount, intl, deliveryDate) =>
      dispatch(sendProjectBid(listingId, transactionId, description, amount, intl, deliveryDate)),
    onShowProfileListing: userId => dispatch(showProfileListing(userId)),
    onSendEnquiryCustomer: (listingId, isLightEntrepreneur, message) =>
      dispatch(sendEnquiryCustomer(listingId, isLightEntrepreneur, message)),
    onSendEnquiryProvider: (listingId, message) =>
      dispatch(sendEnquiryProvider(listingId, message)),
    onJobDoneReleaseFunds: transactionId => dispatch(jobDoneReleaseFunds(transactionId)),
    onUpdateProfile: data => dispatch(updateProfile(data)),
  };
};

const TransactionPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(TransactionPageComponent);

TransactionPage.loadData = loadData;
TransactionPage.setInitialValues = setInitialValues;

export default TransactionPage;
